// src/pages/shop/ShopAnalytics.jsx
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { sales as salesApi, shop as shopApi, products as productsApi } from '../../api';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../../components/BottomNav';
import Icon from '../../components/Icon';
import MiniBars from '../../components/MiniBars';
import { formatMoney } from '../../data/currencies';
import { useMyCurrency, useMyShop, setMyShop } from '../../hooks/useMyShop';
import { useAuth } from '../../hooks/useAuth';
import { useReloadOnFocus } from '../../hooks/useReloadOnFocus';
import { useShopNav } from './ShopHome';
import { WIDGETS, isWidgetOn } from '../../config/dashboardWidgets';

const DAY = 86400000;

// ─── Расчёты на клиенте ───────────────────────────────────────
function itemCost(it) { return (it.costPrice || 0) * (it.qty || 0); }
function saleProfit(s) {
  const cost = (s.items || []).reduce((c, it) => c + itemCost(it), 0);
  return (s.total || 0) - cost;
}

function computeStats(sales, returns) {
  const revenueGross = sales.reduce((s, x) => s + (x.total || 0), 0);
  const profitGross = sales.reduce((s, x) => s + saleProfit(x), 0);
  const retMoney = (returns && returns.money) || 0;
  const retCost = (returns && returns.cost) || 0;
  // Возврат уменьшает выручку (деньги отданы) и прибыль (на разницу между
  // отданными деньгами и себестоимостью вернувшегося на склад товара).
  const revenue = revenueGross - retMoney;
  const profit = profitGross - (retMoney - retCost);
  const count = sales.length;
  const cash = sales.filter(s => !s.paidByPoints);
  const points = sales.filter(s => s.paidByPoints);
  return {
    revenue, profit, count,
    revenueGross, profitGross,
    returns: { money: retMoney, cost: retCost },
    // Средний чек — по валовой выручке: возвраты его не характеризуют.
    avgCheck: count ? Math.round(revenueGross / count) : 0,
    payments: {
      cash: {
        count: cash.length,
        // Фактически полученные деньги: продажа в долг наличными не приходит.
        totalAmount: cash.reduce((s, x) => s + (x.cashAmount != null ? x.cashAmount : (x.total || 0) - (x.creditAmount || 0)), 0),
        creditAmount: cash.reduce((s, x) => s + (x.creditAmount || 0), 0),
        // Разбивка по способу (старые продажи без payMethod → наличные).
        received: (x) => (x.cashAmount != null ? x.cashAmount : (x.total || 0) - (x.creditAmount || 0)),
        byCash: cash.filter(x => (x.payMethod || 'cash') === 'cash')
          .reduce((s, x) => s + (x.cashAmount != null ? x.cashAmount : (x.total || 0) - (x.creditAmount || 0)), 0),
        byCashless: cash.filter(x => x.payMethod === 'cashless')
          .reduce((s, x) => s + (x.cashAmount != null ? x.cashAmount : (x.total || 0) - (x.creditAmount || 0)), 0),
      },
      points: {
        count: points.length,
        totalAmount: points.reduce((s, x) => s + (x.total || 0), 0),
        pointsRedeemed: points.reduce((s, x) => s + (x.pointsRedeemed || 0), 0),
        cashTopUp: points.reduce((s, x) => s + (x.cashAmount || 0), 0),
      },
    },
  };
}

function topProductsOf(sales) {
  const map = {};
  sales.forEach(s => (s.items || []).forEach(it => {
    if (!map[it.name]) map[it.name] = { qty: 0, revenue: 0, unit: it.unit };
    map[it.name].qty += it.qty;
    map[it.name].revenue += (it.salePrice || 0) * (it.qty || 0);
  }));
  return Object.entries(map).sort((a, b) => b[1].revenue - a[1].revenue).slice(0, 8);
}

// «Ноунэйм»: продажи товаров, которых нет в базе. Коды, которые пробивали
// чаще всего, — первые кандидаты завести карточку. Уже заведённые
// (есть в каталоге) не показываем.
function noNameOf(sales) {
  let lines = 0, revenue = 0;
  const codes = new Map();
  for (const s of sales) for (const it of (s.items || [])) {
    if (!it.noName) continue;
    const line = (it.salePrice || 0) * (it.qty || 0) - (it.discountAmount || 0);
    lines++; revenue += line;
    if (it.scannedBarcode) {
      const c = codes.get(it.scannedBarcode) || { barcode: it.scannedBarcode, times: 0, revenue: 0 };
      c.times++; c.revenue += line;
      codes.set(it.scannedBarcode, c);
    }
  }
  const known = new Set();
  for (const p of (productsApi.cached() || [])) {
    if (p.barcode) known.add(p.barcode);
    for (const v of (p.variants || [])) if (v.barcode) known.add(v.barcode);
  }
  const list = [...codes.values()].filter(c => !known.has(c.barcode))
    .sort((a, b) => b.times - a.times || b.revenue - a.revenue).slice(0, 10);
  return { lines, revenue, codes: list };
}

function categoryProfit(sales, noCatLabel) {
  const map = {};
  sales.forEach(s => (s.items || []).forEach(it => {
    const cat = (it.product && (
      (it.product.productCategory && it.product.productCategory.name) || it.product.category
    )) || noCatLabel;
    const net = (it.salePrice || 0) * (it.qty || 0) - (it.discountAmount || 0);
    const profit = net - (it.costPrice || 0) * (it.qty || 0);
    map[cat] = (map[cat] || 0) + profit;
  }));
  return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 8);
}

function revenueByEmployee(sales, ownerLabel) {
  const map = {};
  sales.forEach(s => {
    const who = s.employee ? s.employee.name : ownerLabel;
    map[who] = (map[who] || 0) + (s.total || 0);
  });
  return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 10);
}

function RankedList({ data, currency, color }) {
  const max = Math.max(1, ...data.map(d => Math.abs(d.value)));
  return (
    <div className="card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {data.map((d, i) => (
        <div key={d.name + i}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
            <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{d.name}</span>
            <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', marginLeft: 8 }}>{formatMoney(d.value, currency)}</span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: 'var(--bg-subtle)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.max(2, (Math.abs(d.value) / max) * 100)}%`, background: color, borderRadius: 3 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function trendByDay(sales, periodDays) {
  const now = new Date();
  const buckets = [];
  for (let i = periodDays - 1; i >= 0; i--) {
    const d = new Date(now.getTime() - i * DAY);
    buckets.push({ key: d.toDateString(), label: String(d.getDate()), value: 0, value2: 0 });
  }
  const idx = {}; buckets.forEach((b, i) => (idx[b.key] = i));
  sales.forEach(s => {
    const k = new Date(s.createdAt).toDateString();
    if (idx[k] !== undefined) {
      buckets[idx[k]].value += s.total || 0;
      buckets[idx[k]].value2 += saleProfit(s);
    }
  });
  return buckets;
}

function byHour(sales) {
  const arr = Array.from({ length: 24 }, (_, h) => ({ label: String(h), value: 0 }));
  sales.forEach(s => { arr[new Date(s.createdAt).getHours()].value += 1; });
  return arr;
}

function byWeekday(sales, lang) {
  const fmt = new Intl.DateTimeFormat(lang || 'ru', { weekday: 'short' });
  const ref = [5, 6, 7, 8, 9, 10, 11].map(d => new Date(2024, 0, d)); // 1 янв 2024 = Пн
  const labels = ref.map(d => fmt.format(d));
  const arr = labels.map(l => ({ label: l, value: 0 }));
  sales.forEach(s => {
    const wd = (new Date(s.createdAt).getDay() + 6) % 7; // 0 = Пн
    arr[wd].value += 1;
  });
  return arr;
}

function pct(cur, prev) {
  if (!prev) return cur > 0 ? 100 : 0;
  return Math.round(((cur - prev) / prev) * 100);
}

function Delta({ cur, prev }) {
  const p = pct(cur, prev);
  if (p === 0) return null;
  const up = p > 0;
  return (
    <span style={{ fontSize: 11, fontWeight: 800, marginLeft: 6, color: up ? 'var(--success)' : 'var(--danger)', fontVariantNumeric: 'tabular-nums' }}>
      {up ? '▲' : '▼'} {Math.abs(p)}%
    </span>
  );
}

function SectionLabel({ children }) {
  return (
    <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', letterSpacing: '0.08em', margin: '20px 0 10px 4px' }}>
      {children}
    </p>
  );
}

export default function ShopAnalytics() {
  const { t, i18n } = useTranslation();
  const navItems = useShopNav();
  const currency = useMyCurrency();
  const myShop = useMyShop();
  const { isOwner } = useAuth();
  const navigate = useNavigate();

  const [raw, setRaw] = useState(null);
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);
  const [cfgOpen, setCfgOpen] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  const fetchData = async () => {
    try { return await salesApi.list(period * 2); } catch (e) { return null; }
  };
  const load = async () => { setLoading(true); setRaw(await fetchData()); setLoading(false); };
  useReloadOnFocus(async () => { setRaw(await fetchData()); });

  const on = (k) => isWidgetOn(myShop, k);

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  const allSales = (raw && raw.sales) || [];
  const since = Date.now() - period * DAY;
  const curSales = allSales.filter(s => new Date(s.createdAt).getTime() >= since);
  const prevSales = allSales.filter(s => {
    const ts = new Date(s.createdAt).getTime();
    return ts >= since - period * DAY && ts < since;
  });

  // Возвраты делим по тем же периодам, что и продажи, иначе сравнение соврёт.
  const allReturns = (raw && raw.returns && raw.returns.list) || [];
  const sumReturns = (list) => list.reduce(
    (acc, r) => ({ money: acc.money + (r.money || 0), cost: acc.cost + (r.cost || 0) }),
    { money: 0, cost: 0 },
  );
  const curReturns = sumReturns(allReturns.filter(r => new Date(r.createdAt).getTime() >= since));
  const prevReturns = sumReturns(allReturns.filter(r => {
    const ts = new Date(r.createdAt).getTime();
    return ts >= since - period * DAY && ts < since;
  }));

  const cur = computeStats(curSales, curReturns);
  const prev = computeStats(prevSales, prevReturns);
  const top = topProductsOf(curSales);
  const trend = trendByDay(curSales, period);
  const hours = period === 1 ? byHour(curSales) : byWeekday(curSales, i18n.language);
  const catProfit = categoryProfit(curSales, t('analytics.noCategory'));
  const empRevenue = revenueByEmployee(curSales, t('report.owner'));
  const noName = noNameOf(curSales);
  const showCompare = on('comparison') && prevSales.length > 0;

  const periodLabel = period === 1 ? t('common.today') : period === 7 ? t('common.week') : t('common.month');

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ flex: 1 }}>
          <h1 className="page-title">{t('analytics.title')}</h1>
        </div>
        {isOwner && (
          <button onClick={() => setCfgOpen(true)} className="btn btn-soft" style={{ padding: '8px 10px', fontSize: 13 }}>
            <Icon name="settings" size={15} />
          </button>
        )}
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* Период */}
        <div style={{ display: 'flex', gap: 4, padding: 4, background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)', marginBottom: 16 }}>
          {[{ val: 1, label: t('common.today') }, { val: 7, label: t('common.week') }, { val: 30, label: t('common.month') }].map(p => (
            <button key={p.val} onClick={() => setPeriod(p.val)}
              style={{
                flex: 1, padding: 10,
                background: period === p.val ? 'var(--bg-elevated)' : 'transparent',
                border: 'none', borderRadius: 'var(--r-xs)',
                color: period === p.val ? 'var(--fg)' : 'var(--fg-muted)',
                fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-display)',
                boxShadow: period === p.val ? 'var(--shadow-xs)' : 'none',
              }}>
              {p.label}
            </button>
          ))}
        </div>

        {/* Главная плашка (ядро) */}
        <div className="hero-shop" style={{ marginBottom: 12 }}>
          <p style={{ fontSize: 11, opacity: 0.85, fontWeight: 700, letterSpacing: '0.08em' }}>
            {t('analytics.revenue').toUpperCase()} · {periodLabel.toUpperCase()}
          </p>
          <p className="hero-num" style={{ marginTop: 6 }}>
            {formatMoney(cur.revenue, currency)}
            {showCompare && <Delta cur={cur.revenue} prev={prev.revenue} />}
          </p>
          <div style={{ display: 'flex', gap: 24, marginTop: 16 }}>
            <div>
              <p style={{ fontSize: 11, opacity: 0.7, fontWeight: 600 }}>{t('analytics.profit')}</p>
              <p className="num" style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>
                {formatMoney(cur.profit, currency)}{showCompare && <Delta cur={cur.profit} prev={prev.profit} />}
              </p>
            </div>
            <div>
              <p style={{ fontSize: 11, opacity: 0.7, fontWeight: 600 }}>{t('analytics.salesCount')}</p>
              <p className="num" style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>
                {cur.count}{showCompare && <Delta cur={cur.count} prev={prev.count} />}
              </p>
            </div>
            {cur.count > 0 && (
              <div>
                <p style={{ fontSize: 11, opacity: 0.7, fontWeight: 600 }}>{t('analytics.averageCheck')}</p>
                <p className="num" style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>{formatMoney(cur.avgCheck, currency)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Виджет: динамика по дням */}
        {on('trend') && cur.count > 0 && (
          <>
            <SectionLabel>{t('analytics.w.trend').toUpperCase()}</SectionLabel>
            <div className="card" style={{ padding: 14 }}>
              <MiniBars data={trend} />
              <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11, color: 'var(--fg-faint)' }}>
                <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: 'var(--brand-primary)', opacity: 0.35, marginRight: 4 }} />{t('analytics.revenue')}</span>
                <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: 'var(--brand-primary-strong, #4f46e5)', marginRight: 4 }} />{t('analytics.profit')}</span>
              </div>
            </div>
          </>
        )}

        {/* Виджет: способы оплаты */}
        {on('payments') && cur.count > 0 && (
          <>
            <SectionLabel>{t('analytics.paymentMethods')}</SectionLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 'var(--r-sm)', background: 'var(--success-soft)', color: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon name="wallet" size={20} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 700 }}>{t('analytics.cashPayments')}</p>
                  <p style={{ fontSize: 11, color: 'var(--fg-faint)', fontVariantNumeric: 'tabular-nums' }}>
                    {cur.payments.cash.count} {cur.payments.cash.count === 1 ? t('analytics.saleSingular') : t('analytics.salePlural')}
                    {cur.payments.cash.creditAmount > 0 && (
                      <> · {t('analytics.onCredit', { amount: formatMoney(cur.payments.cash.creditAmount, currency) })}</>
                    )}
                  </p>
                </div>
                <p style={{ fontSize: 16, fontWeight: 800, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>
                  {formatMoney(cur.payments.cash.totalAmount, currency)}
                </p>
              </div>

              {/* Разбивка нал/безнал — показываем, если был хоть один безнал */}
              {cur.payments.cash.byCashless > 0 && (
                <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 'var(--r-sm)', background: 'var(--brand-primary-soft, var(--bg-subtle))', color: 'var(--brand-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon name="creditCard" size={20} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 14, fontWeight: 700 }}>{t('analytics.cashlessPayments', 'Безналичные')}</p>
                    <p style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
                      {t('analytics.cashVsCashless', 'Наличными: {{cash}}', { cash: formatMoney(cur.payments.cash.byCash, currency) })}
                    </p>
                  </div>
                  <p style={{ fontSize: 16, fontWeight: 800, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)', color: 'var(--brand-primary)' }}>
                    {formatMoney(cur.payments.cash.byCashless, currency)}
                  </p>
                </div>
              )}

              {cur.returns.money > 0 && (
                <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 'var(--r-sm)', background: 'var(--warning-soft)', color: 'var(--warning)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon name="refresh" size={20} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 14, fontWeight: 700 }}>{t('products.return')}</p>
                    <p style={{ fontSize: 11, color: 'var(--fg-faint)' }}>{t('analytics.returnsHint')}</p>
                  </div>
                  <p style={{ fontSize: 16, fontWeight: 800, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)', color: 'var(--warning)' }}>
                    −{formatMoney(cur.returns.money, currency)}
                  </p>
                </div>
              )}
              {cur.payments.points.count > 0 && (
                <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 'var(--r-sm)', background: 'var(--brand-accent-soft)', color: 'var(--brand-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon name="starFilled" size={20} stroke={0} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 14, fontWeight: 700 }}>{t('analytics.pointsPayments')}</p>
                    <p style={{ fontSize: 11, color: 'var(--fg-faint)', fontVariantNumeric: 'tabular-nums' }}>
                      {cur.payments.points.count} {cur.payments.points.count === 1 ? t('analytics.saleSingular') : t('analytics.salePlural')}
                      {cur.payments.points.cashTopUp > 0 && (<> · {t('analytics.fromCashTopUp', { amount: formatMoney(cur.payments.points.cashTopUp, currency) })}</>)}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: 16, fontWeight: 800, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>
                      {formatMoney(cur.payments.points.totalAmount, currency)}
                    </p>
                    <p style={{ fontSize: 10, color: 'var(--brand-accent)', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                      −{cur.payments.points.pointsRedeemed.toLocaleString()} ⭐
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Виджет: продажи по часам / дням недели */}
        {on('hours') && cur.count > 0 && (
          <>
            <SectionLabel>{(period === 1 ? t('analytics.w.byHour') : t('analytics.w.byWeekday')).toUpperCase()}</SectionLabel>
            <div className="card" style={{ padding: 14 }}>
              <MiniBars data={hours} height={120} color="var(--brand-accent)" color2="var(--brand-accent)" />
              <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 6 }}>{t('analytics.w.byHourHint')}</p>
            </div>
          </>
        )}

        {/* Виджет: топ товаров */}
        {on('topProducts') && top.length > 0 && (
          <>
            <SectionLabel>{t('analytics.topProducts')} {periodLabel.toUpperCase()}</SectionLabel>
            <div className="card" style={{ padding: 0 }}>
              {top.map(([name, data], i) => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: i < top.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 999, background: i === 0 ? 'var(--brand-primary-soft)' : 'var(--bg-subtle)', color: i === 0 ? 'var(--brand-primary)' : 'var(--fg-muted)', fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'var(--font-display)' }}>{i + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</p>
                    <p style={{ fontSize: 11, color: 'var(--fg-faint)', fontVariantNumeric: 'tabular-nums' }}>{t('common.sold', { qty: data.qty, unit: data.unit })}</p>
                  </div>
                  <p style={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>{formatMoney(data.revenue, currency)}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Продажи без карточки товара («ноунэйм») — только если были */}
        {noName.lines > 0 && (
          <>
            <SectionLabel>{t('analytics.noName.title').toUpperCase()}</SectionLabel>
            <div className="card" style={{ padding: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, borderBottom: noName.codes.length ? '1px solid var(--border-subtle)' : 'none' }}>
                <div style={{ width: 40, height: 40, borderRadius: 'var(--r-sm)', background: 'var(--warning-soft)', color: 'var(--warning)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 20, fontWeight: 800 }}>?</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 700 }}>{t('analytics.noName.lines', { count: noName.lines })}</p>
                  <p style={{ fontSize: 11, color: 'var(--fg-faint)', lineHeight: 1.4 }}>{t('analytics.noName.hint')}</p>
                </div>
                <p style={{ fontSize: 16, fontWeight: 800, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>{formatMoney(noName.revenue, currency)}</p>
              </div>
              {noName.codes.length > 0 && (
                <>
                  <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', padding: '10px 16px 4px' }}>{t('analytics.noName.codesTitle')}</p>
                  {noName.codes.map((c, i) => (
                    <div key={c.barcode} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{c.barcode}</p>
                        <p style={{ fontSize: 11, color: 'var(--fg-faint)' }}>{t('analytics.noName.times', { count: c.times, amount: formatMoney(c.revenue, currency) })}</p>
                      </div>
                      <button className="btn btn-soft btn-sm" style={{ width: 'auto', flexShrink: 0 }}
                        onClick={() => navigate('/products', { state: { newBarcode: c.barcode } })}>
                        <Icon name="plus" size={14} /> {t('analytics.noName.create')}
                      </button>
                    </div>
                  ))}
                </>
              )}
            </div>
          </>
        )}

        {/* Виджет: прибыль по категориям */}

        {on('category') && catProfit.length > 0 && (
          <>
            <SectionLabel>{t('analytics.w.category').toUpperCase()}</SectionLabel>
            <RankedList data={catProfit} currency={currency} color="var(--brand-primary-strong, #4f46e5)" />
          </>
        )}

        {/* Виджет: выручка по сотрудникам */}
        {on('employees') && empRevenue.length > 0 && (
          <>
            <SectionLabel>{t('analytics.w.employees').toUpperCase()}</SectionLabel>
            <RankedList data={empRevenue} currency={currency} color="var(--brand-primary)" />
          </>
        )}

        {cur.count === 0 && (
          <div className="empty-state">
            <div className="icon"><Icon name="chart" size={28} stroke={1.5} /></div>
            <p>{t('analytics.noSales')}</p>
          </div>
        )}
      </div>

      {cfgOpen && <WidgetSettingsSheet shop={myShop} onClose={() => setCfgOpen(false)} t={t} />}

      <BottomNav items={navItems} />
    </div>
  );
}

// ─── Шит настройки виджетов (владелец) ────────────────────────
function WidgetSettingsSheet({ shop, onClose, t }) {
  const [saving, setSaving] = useState(null);

  const toggle = async (key, next) => {
    setSaving(key);
    try {
      const cfg = { ...((shop && shop.dashboardConfig) || {}), [key]: next };
      const updated = await shopApi.updateDashboard(cfg);
      setMyShop(updated);
    } catch (e) {
      /* тихо */
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-sheet">
        <h3 className="modal-title">{t('analytics.customize')}</h3>
        <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 12 }}>{t('analytics.customizeHint')}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {WIDGETS.map(w => {
            const value = isWidgetOn(shop, w.key);
            return (
              <div key={w.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 4px' }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{t(w.labelKey)}</span>
                <button onClick={() => saving !== w.key && toggle(w.key, !value)}
                  style={{ width: 44, height: 26, borderRadius: 999, border: 'none', cursor: 'pointer', background: value ? 'var(--brand-primary)' : 'var(--bg-subtle)', position: 'relative', transition: 'background 0.15s', flexShrink: 0 }}>
                  <span style={{ position: 'absolute', top: 3, left: value ? 21 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.15s', boxShadow: 'var(--shadow-xs)' }} />
                </button>
              </div>
            );
          })}
        </div>
        <div className="modal-actions" style={{ marginTop: 16 }}>
          <button className="btn btn-primary" onClick={onClose} style={{ width: '100%' }}>{t('common.done')}</button>
        </div>
      </div>
    </div>
  );
}
